/**
 * Lazy Loading Global Page Settings Composable
 *
 * Uses shallowRef to prevent SSR serialization into __NUXT_DATA__.
 * On SSR: Settings are fetched and held in memory (not serialized to HTML).
 * On Client: Settings are fetched lazily during idle time.
 *
 * This reduces initial HTML payload by ~216KB.
 */

import { shallowRef, type ShallowRef } from 'vue';
import type { MinifiedGlobalPageSettings } from '~~/lib/ContentfulGraphqlService';
import { applyContentfulAssetCdnByLocale } from '~/utils/contentful/rewriteContentfulAssetHostsByLocale';

// Symbol key for Nuxt app-level storage (per-request isolation on SSR)
const GPS_KEY = '__lazyGlobalPageSettings';

interface LazyGpsState {
  globalPageSettings: ShallowRef<MinifiedGlobalPageSettings | null>;
  isLoading: ShallowRef<boolean>;
  isHydrated: ShallowRef<boolean>;
  loadedLocale: string | null;
  loadPromise: Promise<void> | null;
}

/**
 * Get or create the app-scoped state.
 * On server: Uses nuxtApp.payload for per-request isolation.
 * On client: Uses module-level singleton.
 */
function getState(): LazyGpsState {
  const nuxtApp = useNuxtApp();

  // Check if state already exists for this app instance
  if (nuxtApp[GPS_KEY]) {
    return nuxtApp[GPS_KEY] as LazyGpsState;
  }

  // Create new state
  const state: LazyGpsState = {
    globalPageSettings: shallowRef<MinifiedGlobalPageSettings | null>(null),
    isLoading: shallowRef(false),
    isHydrated: shallowRef(false),
    loadedLocale: null,
    loadPromise: null
  };

  // Store in nuxtApp for per-request isolation
  nuxtApp[GPS_KEY] = state;

  return state;
}

/**
 * Fetch global page settings from the API
 */
async function fetchGlobalPageSettings(
  locale: string
): Promise<MinifiedGlobalPageSettings | null> {
  const logger = useLogger();

  try {
    const settings = await $fetch<MinifiedGlobalPageSettings>(
      '/api/contentful/globalPageSettings',
      {
        query: { locale }
      }
    );
    return settings ?? null;
  } catch (error) {
    logger.error('[useLazyGlobalPageSettings] Failed to fetch settings', {
      locale,
      error
    });
    return null;
  }
}

/**
 * Get the lazy-loaded global page settings.
 *
 * On server: Fetches and stores in shallowRef (not serialized to HTML).
 * On client: Returns existing ref, triggers lazy load if needed.
 */
export function useLazyGlobalPageSettings() {
  const state = getState();

  /**
   * Load settings - used both on server and client
   */
  const load = async (locale: string) => {
    // Avoid duplicate requests
    if (state.loadPromise) {
      await state.loadPromise;
      return;
    }

    if (state.globalPageSettings.value && state.loadedLocale === locale) {
      state.globalPageSettings.value = applyContentfulAssetCdnByLocale(
        state.globalPageSettings.value,
        locale
      );
      state.isHydrated.value = true;
      return;
    }

    state.isLoading.value = true;

    state.loadPromise = (async () => {
      try {
        const settings = await fetchGlobalPageSettings(locale);
        state.globalPageSettings.value = settings
          ? applyContentfulAssetCdnByLocale(settings, locale)
          : null;
        state.loadedLocale = locale;
        state.isHydrated.value = true;
      } finally {
        state.isLoading.value = false;
        state.loadPromise = null;
      }
    })();

    await state.loadPromise;
  };

  /**
   * Inject pre-loaded settings (SSR direct service load).
   */
  const set = (settings: MinifiedGlobalPageSettings | null, locale: string) => {
    state.globalPageSettings.value = settings;
    state.loadedLocale = locale;
    state.isHydrated.value = true;
    state.isLoading.value = false;
    state.loadPromise = null;
  };

  /**
   * Schedule lazy load on client during idle time
   */
  const scheduleLazyLoad = (locale: string) => {
    if (import.meta.server) {
      return;
    }

    // Already loaded
    if (state.globalPageSettings.value && state.isHydrated.value) {
      return;
    }

    import('~/utils/requestIdleCallback').then(({ requestIdleCallback }) => {
      requestIdleCallback(
        async () => {
          await load(locale);
        },
        { timeout: 2000 } // Max 2 seconds wait
      );
    });
  };

  return {
    /** The global page settings (reactive shallowRef) */
    globalPageSettings: state.globalPageSettings,
    /** Whether settings are currently being loaded */
    isLoading: readonly(state.isLoading),
    /** Whether client-side hydration is complete */
    isHydrated: readonly(state.isHydrated),
    /** Load settings immediately */
    load,
    /** Inject pre-loaded settings without HTTP fetch */
    set,
    /** Schedule lazy load during idle time */
    scheduleLazyLoad
  };
}
